import React from 'react';
import { Box, Container, Typography, Link } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';

// Helper function to get AppBar height
const getAppBarHeight = () => 64;

export default function ContactSection() {
  const appBarHeight = getAppBarHeight();

  // Content for the merged consultation and contact section
  const consultationContent = {
    title: "Ready to Start a Project?", 
    body: "<b>Contact us</b> today for a free consultation and let's discuss how our <b>team and services</b> can help you <b>realise your goals!</b>",
    mail: true 
  };

  return (
    <Box id="contact-section" className="snap-section" sx={{ 
      position: 'relative',
      background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #2561b3 100%)',
      color: '#FFFFFF',
      paddingTop: `${appBarHeight}px`,
      paddingY: 10,
      overflow: 'hidden'
    }}>
      {/* Animated background elements */}
      <Box sx={{
        position: 'absolute',
        top: '10%',
        right: '10%',
        width: 250,
        height: 250,
        background: 'linear-gradient(135deg, rgba(37, 97, 179, 0.3), rgba(96, 165, 250, 0.2))',
        borderRadius: '50%',
        animation: 'floatAndPulse 8s ease-in-out infinite',
        filter: 'blur(40px)'
      }} />
      <Box sx={{
        position: 'absolute',
        bottom: '20%',
        left: '5%',
        width: 180,
        height: 180,
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(37, 97, 179, 0.3))',
        borderRadius: '50%',
        animation: 'floatAndPulse 6s ease-in-out infinite reverse',
        filter: 'blur(30px)'
      }} />
      
      {/* Additional floating elements for more movement */}
      <Box sx={{
        position: 'absolute',
        top: '30%',
        left: '15%',
        width: 100,
        height: 100,
        background: 'linear-gradient(45deg, rgba(96, 165, 250, 0.15), rgba(37, 97, 179, 0.1))',
        transform: 'rotate(45deg)',
        animation: 'floatRotateAndPulse 12s ease-in-out infinite',
        filter: 'blur(20px)'
      }} />
      <Box sx={{
        position: 'absolute',
        bottom: '30%',
        right: '20%',
        width: 90,
        height: 90,
        background: 'linear-gradient(135deg, rgba(37, 97, 179, 0.15), transparent)',
        borderRadius: '50%',
        animation: 'floatAndPulse 10s ease-in-out infinite reverse',
        filter: 'blur(25px)'
      }} />
      <Box sx={{
        position: 'absolute',
        top: '60%',
        right: '8%',
        width: 70,
        height: 70,
        background: 'linear-gradient(45deg, rgba(59, 130, 246, 0.15), transparent)',
        transform: 'rotate(30deg)',
        animation: 'floatRotateAndPulse 14s ease-in-out infinite',
        filter: 'blur(15px)'
      }} />
      <Box sx={{
        position: 'absolute',
        top: '80%',
        left: '12%',
        width: 60,
        height: 60,
        background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.2), transparent)',
        borderRadius: '50%',
        animation: 'floatAndPulse 9s ease-in-out infinite reverse',
        filter: 'blur(20px)'
      }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Box sx={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
          backdropFilter: 'blur(20px)',
          borderRadius: 4,
          padding: { xs: 6, md: 8 },
          border: '1px solid rgba(255,255,255,0.2)',
          boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
          textAlign: 'center'
        }}>
          <Typography 
            variant="h2" 
            component="h2" 
            sx={{ 
              fontWeight: 800,
              background: 'linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              marginBottom: 3,
              fontSize: { xs: '2.5rem', md: '3.5rem' }
            }}
          >
            {consultationContent.title}
          </Typography>
          
          <Box sx={{ 
            width: 120, 
            height: 4, 
            background: 'linear-gradient(90deg, #60a5fa, #ffffff)',
            margin: '0 auto 4rem auto',
            borderRadius: 2
          }} />
          
          <Typography 
            variant="h5" 
            component="p" 
            dangerouslySetInnerHTML={{ __html: consultationContent.body }}
            sx={{ 
              marginBottom: 6,
              color: 'rgba(255, 255, 255, 0.9)',
              maxWidth: '700px', 
              marginX: 'auto',
              lineHeight: 1.7,
              fontSize: { xs: '1.2rem', md: '1.4rem' }
            }}
          />
          
          {/* Contact Information - enhanced styling */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 3, md: 6 },
            marginBottom: 6
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              padding: 2,
              borderRadius: 2,
              background: 'rgba(255, 255, 255, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.15)',
                transform: 'translateY(-2px)'
              }
            }}>
              <EmailIcon sx={{ marginRight: 1.5, color: 'inherit', fontSize: '2rem' }} />
              <Link 
                target='_blank' 
                href="mailto:contact@datasky.uk?subject=New enquiry"
                variant="h6"
                sx={{ 
                  color: 'inherit', 
                  fontWeight: 600,
                  textDecoration: 'none',
                  '&:hover': { 
                    color: '#60a5fa'
                  } 
                }}
              >
                contact@datasky.uk
              </Link>
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              padding: 2,
              borderRadius: 2,
              background: 'rgba(255, 255, 255, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.15)',
                transform: 'translateY(-2px)'
              }
            }}>
              <TwitterIcon sx={{ marginRight: 1.5, color: 'inherit', fontSize: '2rem' }} />
              <Link 
                target='_blank' 
                href="https://twitter.com/dataskyuk"
                variant="h6"
                sx={{ 
                  color: 'inherit', 
                  fontWeight: 600,
                  textDecoration: 'none',
                  '&:hover': { 
                    color: '#60a5fa'
                  } 
                }}
              >
                @dataskyuk
              </Link>
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              padding: 2,
              borderRadius: 2,
              background: 'rgba(255, 255, 255, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.15)',
                transform: 'translateY(-2px)'
              }
            }}>
              <LinkedInIcon sx={{ marginRight: 1.5, color: 'inherit', fontSize: '2rem' }} />
              <Link 
                target='_blank' 
                href="https://www.linkedin.com/company/datasky-uk/"
                variant="h6"
                sx={{ 
                  color: 'inherit', 
                  fontWeight: 600,
                  textDecoration: 'none',
                  '&:hover': { 
                    color: '#60a5fa'
                  } 
                }}
              >
                /datasky-uk
              </Link>
            </Box>
          </Box>
          
          {/* Company info with elegant styling */}
          <Box sx={{
            borderTop: '1px solid rgba(255, 255, 255, 0.2)',
            paddingTop: 4,
            textAlign: 'center'
          }}>
            <Typography variant="h6" sx={{ 
              marginBottom: 1,
              fontWeight: 600,
              color: 'rgba(255, 255, 255, 0.9)'
            }}>
              datasky limited
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '1.1rem'
            }}>
              Glasgow, Scotland
            </Typography>
          </Box>
        </Box>
      </Container>
      
      {/* Add keyframes for floating animation */}
      <style>{`
        @keyframes floatAndPulse {
          0%, 100% { 
            transform: translateY(0px) scale(1); 
          }
          25% { 
            transform: translateY(-25px) scale(1.1); 
          }
          50% { 
            transform: translateY(-15px) scale(0.9); 
          }
          75% { 
            transform: translateY(-30px) scale(1.05); 
          }
        }
        @keyframes floatRotateAndPulse {
          0%, 100% { 
            transform: translateY(0px) rotate(45deg) scale(1); 
          }
          25% { 
            transform: translateY(-25px) rotate(47deg) scale(1.15); 
          }
          50% { 
            transform: translateY(-15px) rotate(44deg) scale(0.85); 
          }
          75% { 
            transform: translateY(-30px) rotate(46deg) scale(1.1); 
          }
        }
      `}</style>
    </Box>
  );
} 